import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { apiGet, apiPost } from '../lib/api';
import { Feedback } from '../components/Feedback';
import { LoadingDots } from '../components/LoadingDots';
import { useAuth } from '../context/AuthContext';

interface Device {
  id: number;
  deviceId: number;
  name: string | null;
  schemaCode: string | null;
  schemaName: string | null;
  schemaId: number | null;
  tier: number;
  locationLat: number | null;
  locationLng: number | null;
  lastSeen: number | null;
}

interface DevicesResp { devices: Device[] }
interface Certification { schema: string; tier: number; status: string; expiresAt: number | null }
interface AvailableSchema { id: number; code: string; maxTier: number }

const TIER_NAMES: Record<number, string> = { 0: 'NANO', 1: 'MICRO', 2: 'FULL' };

function fmtId(n: number): string {
  return '0x' + n.toString(16).toUpperCase().padStart(6, '0');
}

function fmtSeen(unix: number | null): string {
  if (!unix) return 'never';
  const diffSec = Math.floor((Date.now() - unix) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}min ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}

/** Route: `/dashboard/devices`. Lists registered devices and exposes a registration form. */
export function DevicesPage() {
  const { canUseSchema } = useAuth();
  const [devices, setDevices] = useState<Device[] | null>(null);
  const [openId, setOpenId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  // Form fields
  const [deviceIdInput, setDeviceIdInput] = useState('');
  const [tier, setTier] = useState(0);
  const [name, setName] = useState('');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [schemaId, setSchemaId] = useState<number | null>(null);
  const [schemas, setSchemas] = useState<AvailableSchema[]>([]);

  function refresh() {
    apiGet<DevicesResp>('/v1/devices').then((r) => setDevices(r.devices)).catch((err: Error) => {
      setDevices((previous) => previous ?? []);
      setFeedback({ type: 'error', msg: err.message || 'Unable to load devices.' });
    });
  }

  useEffect(() => {
    refresh();
    Promise.all([
      apiGet<{ schemas: Array<{ id: number; code: string; implemented?: boolean }> }>('/v1/schemas'),
      apiGet<Certification[]>('/v1/certs'),
    ])
      .then(([schemaResponse, certifications]) => {
        const now = Math.floor(Date.now() / 1000);
        const access = new Map<string, number>();
        for (const cert of certifications) if (cert.status === 'active' && (!cert.expiresAt || cert.expiresAt > now) && canUseSchema(cert.schema)) access.set(cert.schema, Math.max(access.get(cert.schema) ?? -1, cert.tier));
        const availableSchemas = schemaResponse.schemas.flatMap((schema) => schema.implemented !== false && access.has(schema.code) ? [{ id: schema.id, code: schema.code, maxTier: access.get(schema.code)! }] : []);
        setSchemas(availableSchemas);
        const firstSchema = availableSchemas[0];
        if (firstSchema) { setSchemaId(firstSchema.id); setTier((current) => Math.min(current, firstSchema.maxTier)); }
      })
      .catch((err: Error) => setFeedback({ type: 'error', msg: err.message || 'Unable to load the device schema.' }));
  }, [canUseSchema]);

  const selectedSchema = schemas.find((schema) => schema.id === schemaId);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setFeedback(null);
    const normalizedId = deviceIdInput.trim();
    if (!/^(?:0x[0-9a-f]+|[0-9]+)$/i.test(normalizedId)) { setFeedback({ type: 'error', msg: 'Enter a complete hexadecimal or decimal device ID.' }); return; }
    const idNum = Number(normalizedId);
    if (!Number.isFinite(idNum) || idNum < 0 || idNum > 0xFFFFFF) {
      setFeedback({ type: 'error', msg: 'device id must be hex 0x000000 to 0xFFFFFF' });
      return;
    }
    if (tier === 0 && idNum > 0xFFFF) {
      setFeedback({ type: 'error', msg: 'NANO device IDs are limited to 0xFFFF. Select MICRO or FULL for a larger ID.' });
      return;
    }
    if (schemaId === null) {
      setFeedback({ type: 'error', msg: 'schema not loaded yet' });
      return;
    }
    const body: Record<string, unknown> = { deviceId: idNum, schemaId, tier };
    if (name) body.name = name;
    if (lat.trim() || lng.trim()) {
      const latitude = Number(lat);
      const longitude = Number(lng);
      if (!lat.trim() || !lng.trim() || !Number.isFinite(latitude) || !Number.isFinite(longitude)
        || latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) {
        setFeedback({ type: 'error', msg: 'Enter both coordinates: latitude from -90 to 90 and longitude from -180 to 180.' });
        return;
      }
      body.locationLat = latitude;
      body.locationLng = longitude;
    }
    try {
      await apiPost('/v1/devices', body);
      setFeedback({ type: 'success', msg: 'Device registered.' });
      setShowForm(false);
      setDeviceIdInput(''); setName(''); setLat(''); setLng(''); setTier(0);
      refresh();
    } catch (err) {
      setFeedback({ type: 'error', msg: err instanceof Error ? err.message : 'failed to register' });
    }
  }

  return (
    <div>
      <div className="text-muted" style={{ marginBottom: 8 }}>── registered devices ────────────────────────────────────</div>
      <button type="button" className="btn btn-primary" style={{ marginBottom: 16 }} onClick={() => setShowForm((s) => !s)}>
        register device {showForm ? '↑' : '↓'}
      </button>

      {feedback && <Feedback type={feedback.type} message={feedback.msg} />}

      {showForm && (
        <form onSubmit={handleSubmit} className="box" style={{ marginBottom: 16 }}>
          <div className="form-row">
            <label className="label" htmlFor="dev-id">device id (hex)</label>
            <input id="dev-id" className="form-input" value={deviceIdInput} onChange={(e) => setDeviceIdInput(e.target.value)} placeholder="0x000001" inputMode="text" autoComplete="off" />
            <p className="form-hint">NANO: 0x0000 to 0xFFFF · MICRO / FULL: 0x000000 to 0xFFFFFF</p>
          </div>
          <div className="form-row">
            <label className="label" htmlFor="device-schema">schema</label>
            <select id="device-schema" className="form-input" value={schemaId ?? ''} disabled={schemas.length === 0} onChange={(e) => { const nextId = Number(e.target.value); const next = schemas.find((schema) => schema.id === nextId); setSchemaId(nextId); if (next) setTier((current) => Math.min(current, next.maxTier)); }}>{schemas.length === 0 && <option value="">no certified schemas</option>}{schemas.map((schema) => <option key={schema.id} value={schema.id}>{schema.code}</option>)}</select>
          </div>
          <div className="form-row">
            <label className="label" htmlFor="dev-tier">tier</label>
            <select id="dev-tier" className="form-input" value={tier} onChange={(e) => setTier(Number(e.target.value))}>
              <option value="0">0 NANO</option>
              {(selectedSchema?.maxTier ?? -1) >= 1 && <option value="1">1 MICRO</option>}
              {(selectedSchema?.maxTier ?? -1) >= 2 && <option value="2">2 FULL</option>}
            </select>
          </div>
          <div className="form-row">
            <label className="label" htmlFor="dev-name">name (optional)</label>
            <input id="dev-name" className="form-input" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="grid-2">
            <div className="form-row">
              <label className="label" htmlFor="dev-lat">lat</label>
              <input id="dev-lat" className="form-input" value={lat} onChange={(e) => setLat(e.target.value)} placeholder="39.978371" inputMode="decimal" />
            </div>
            <div className="form-row">
              <label className="label" htmlFor="dev-lng">lng</label>
              <input id="dev-lng" className="form-input" value={lng} onChange={(e) => setLng(e.target.value)} placeholder="-86.118097" inputMode="decimal" />
            </div>
          </div>
          <button type="submit" className="btn btn-primary" disabled={!selectedSchema} style={{ marginRight: 8 }}>register</button>
          <button type="button" className="btn" onClick={() => setShowForm(false)}>cancel</button>
        </form>
      )}

      {devices === null ? (
        <LoadingDots />
      ) : devices.length === 0 ? (
        <p className="text-muted">no devices registered yet. click "register device" above to add one.</p>
      ) : (
        <div style={{ marginBottom: 16, overflowX: 'auto' }}>
          <div
            className="text-muted"
            style={{
              display: 'grid',
              gridTemplateColumns: '1.4fr 1.4fr 0.8fr 0.6fr 1fr',
              gap: 8,
              padding: 'var(--spacing-sm) 0',
              borderBottom: '1px solid var(--color-border)',
              fontSize: 'var(--font-size-sm)',
              minWidth: 560,
            }}
          >
            <span>device id</span>
            <span>name</span>
            <span>schema</span>
            <span>tier</span>
            <span>last seen</span>
          </div>
          {devices.map((d) => (
            <div key={d.id} className="device-row" role="button" tabIndex={0} aria-expanded={openId === d.id} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenId(openId === d.id ? null : d.id); } }} onClick={() => setOpenId(openId === d.id ? null : d.id)} style={{ minWidth: 560 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1.4fr 0.8fr 0.6fr 1fr', gap: 8 }}>
                <span className="mono">{fmtId(d.deviceId)}</span>
                <span>{d.name ?? '—'}</span>
                <span>{d.schemaCode ?? '—'}</span>
                <span>{d.tier}</span>
                <span className="text-muted">{fmtSeen(d.lastSeen)}</span>
              </div>
              <div className={`device-detail ${openId === d.id ? 'open' : ''}`} hidden={openId !== d.id}>
                <div className="device-detail-inner">
                  <div className="text-muted">── device {fmtId(d.deviceId)} ─────────────────────────────────────</div>
                  <div className="grid-2">
                    <div><span className="label">name</span>{d.name ?? '—'}</div>
                    <div><span className="label">schema</span>{d.schemaCode} · Tier {d.tier} {TIER_NAMES[d.tier]}</div>
                    <div><span className="label">location</span>{d.locationLat !== null && d.locationLng !== null ? `${d.locationLat.toFixed(4)}°, ${d.locationLng.toFixed(4)}°` : '—'}</div>
                    <div><span className="label">last seen</span>{fmtSeen(d.lastSeen)}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
